import React from "react"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import type { StopPhase } from "@/lib/stopAutomations"
import { useTranslation } from "react-i18next"

// Translated at render.
export const PHASE_LABEL_KEY = {
  arrival: "maps.stopAutomationFields.onArrival",
  departure: "maps.stopAutomationFields.onDeparture",
} as const

export type StopAutomationFieldsProps = {
  phase: StopPhase
  onPhaseChange: (phase: StopPhase) => void
  message: string
  onMessageChange: (message: string) => void
  disabled?: boolean
  error?: string | null
  /** Unique per mounted form, so the label always points at its own textarea. */
  fieldId?: string
}

/** The two fields that are genuinely the dispatcher's choice: when, and what. */
export const StopAutomationFields: React.FC<StopAutomationFieldsProps> = ({
  phase,
  onPhaseChange,
  message,
  onMessageChange,
  disabled = false,
  error,
  fieldId = "stop-automation-message",
}) => {
  const { t } = useTranslation()
  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <Label>{t("maps.stopAutomationFields.when")}</Label>
        <div className="flex gap-2">
          {(["arrival", "departure"] as StopPhase[]).map((option) => (
            <Button
              key={option}
              type="button"
              variant={phase === option ? "default" : "outline"}
              size="sm"
              aria-pressed={phase === option}
              disabled={disabled}
              onClick={() => onPhaseChange(option)}
            >
              {t(PHASE_LABEL_KEY[option])}
            </Button>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">
          {t("maps.stopAutomationFields.runsOnce")}
        </p>
      </div>

      <div className="space-y-1">
        <Label htmlFor={fieldId}>
          {t("maps.stopAutomationFields.instructionLabel")}
        </Label>
        <Textarea
          id={fieldId}
          rows={3}
          value={message}
          disabled={disabled}
          onChange={(e) => onMessageChange(e.target.value)}
          placeholder={t("maps.stopAutomationFields.instructionPlaceholder")}
        />
        <p className="text-xs text-muted-foreground">
          {t("maps.stopAutomationFields.instructionHint")}
        </p>
      </div>

      {error && (
        <p className="text-xs text-red-500" role="alert">
          {error}
        </p>
      )}
    </div>
  )
}

export default StopAutomationFields
